import type { PatternPreset } from '~/types/presets';
import type { DevicePattern } from '~/types/midi';
import musicData from '~/data/musicData';
import { PATTERNS, type PatternParts } from '~/types/pattern';

/**
 * "OG" Pattern preset for MIDI Hardware Devices
 *
 * This preserves the original default pattern from src/config/defaults/midi.json
 */

const ogHardwarePattern: DevicePattern = {
  rootNote: musicData.rootNotes.find(n => n.note === 'G♯') || musicData.rootNotes[0],
  scale: musicData.scales.find(s => s.id === 'pentatonic-maj') || musicData.scales[0],
  scaleMode: 'Major',
  pattern: PATTERNS.find(p => p.name === 'ABCB') || PATTERNS[0],
  patternParts: {
    A: [
      { note: 'G#2', duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'A#2', duration: '8n' },
      { note: 'A#2', duration: '8n' },
    ],
    B: [
      { note: null, duration: '8n' },
      { note: 'C2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
    ],
    C: [
      { note: 'F2', duration: '8n' },
      { note: 'F2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
    ],
    D: [
      { note: 'A#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'C2', duration: '8n' },
      { note: 'C2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: null, duration: '8n' },
    ],
  } as PatternParts,
  beatsPerBar: 4,
  tempo: 110,
  swing: 0,
  space: 35,
  spaceMin: 20,
  spaceMax: 50,
  dissonance: 2,
  dissonanceMin: 0,
  dissonanceMax: 2,
  spread: 7,
  spreadMin: 0,
  spreadMax: 24,
  range: 12,
  rangeMin: 12,
  rangeMax: 37,
  scrambleToggles: {
    scrambleStructure: true,
    scrambleRootNote: true,
    scrambleMode: true,
    scrambleScale: true,
    scrambleSpace: true,
    scrambleDissonance: true,
    scrambleSpread: true,
    scrambleRange: true,
  },
};

/**
 * "OG" Hardware Pattern Preset
 */
export const OG_HARDWARE_PATTERN_PRESET: PatternPreset = {
  id: 'og-hardware-pattern',
  name: 'OG',
  pattern: ogHardwarePattern,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "OG" Hardware pattern preset
 * Returns a deep copy to avoid mutation
 */
export function getOGHardwarePattern(): PatternPreset {
  return {
    ...OG_HARDWARE_PATTERN_PRESET,
    pattern: {
      ...OG_HARDWARE_PATTERN_PRESET.pattern,
      pattern: { ...OG_HARDWARE_PATTERN_PRESET.pattern.pattern },
      patternParts: Object.fromEntries(
        Object.entries(OG_HARDWARE_PATTERN_PRESET.pattern.patternParts).map(([key, value]) => [
          key,
          value.map(note => ({ ...note })),
        ])
      ) as PatternParts,
      scrambleToggles: { ...OG_HARDWARE_PATTERN_PRESET.pattern.scrambleToggles },
    },
  };
}
